import { ref } from "vue";
import { useStartWorkflowRun } from "@/queries/workflows";
import { ApiError } from "@/helpers/api-error";
import type { ReadinessIssue } from "@/types/readiness";

/** Thrown when the user dismisses the pre-run confirm — distinguishes a deliberate cancel from a real failure. */
export class RunCancelledError extends Error {
  constructor() {
    super("Run cancelled by user");
    this.name = "RunCancelledError";
  }
}

export interface RunVars {
  workflowId: string;
  projectId: string;
  projectContextId?: string;
  // Per-run {{ input.* }} values (Run modal / Saved Call). Spread straight into the run request; the
  // readiness retry preserves them since both attempts spread the same vars.
  inputs?: Record<string, unknown>;
}

/**
 * Starts a workflow run with the safe-by-default readiness gate (ADR-033). Attempts the run without
 * override; on a 422 readiness rejection it opens the pre-run confirm and awaits the user — "Run
 * anyway" resubmits with the override, Cancel throws {@link RunCancelledError}. The single dialog is
 * driven by the returned reactive state, so every run trigger gets the same confirm.
 */
export function useRunWithReadinessConfirm() {
  const { startWorkflowRunAsync } = useStartWorkflowRun();

  const confirmOpen = ref(false);
  const issues = ref<ReadinessIssue[]>([]);
  const advisories = ref<ReadinessIssue[]>([]);
  const submitting = ref(false);
  let decide: ((override: boolean) => void) | null = null;

  async function run(vars: RunVars): Promise<string> {
    submitting.value = true;
    try {
      return await startWorkflowRunAsync({ ...vars, overrideReadiness: false });
    } catch (e) {
      if (e instanceof ApiError && e.isRunNotReady) {
        // Release the busy state while the dialog awaits the user, so its own buttons are enabled.
        submitting.value = false;
        issues.value = e.readinessIssues;
        advisories.value = e.readinessAdvisories;
        confirmOpen.value = true;

        const override = await new Promise<boolean>((resolve) => { decide = resolve; });
        decide = null;
        confirmOpen.value = false;
        if (!override) throw new RunCancelledError();

        submitting.value = true; // re-arm for the override retry
        return await startWorkflowRunAsync({ ...vars, overrideReadiness: true });
      }
      throw e;
    } finally {
      submitting.value = false;
    }
  }

  const runAnyway = () => decide?.(true);
  const cancel = () => decide?.(false);

  return { run, confirmOpen, issues, advisories, submitting, runAnyway, cancel };
}
